import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout.jsx';
import { TextField } from '../components/Field.jsx';
import { ErrorAlert, InfoAlert } from '../components/States.jsx';
import useForm from '../hooks/useForm.js';
import { useAuth } from '../store/useAuth.js';
import { useGetSetupQuery } from '../store/apiSlice.js';
import { loginSchema } from '../validation/schemas.js';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: setup } = useGetSetupQuery();

  const form = useForm({ email: '', password: '' }, loginSchema);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');

  if (user) return <Navigate to={location.state?.from?.pathname || '/'} replace />;

  const submit = async (event) => {
    event.preventDefault();
    setFailure('');
    if (!form.validate()) return;

    setBusy(true);
    try {
      await login(form.values);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      // 401 carries a blank `email` key so both inputs go red without repeating
      // the same sentence twice. The banner says it once.
      form.applyServerErrors(err.details);
      setFailure(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the email address your administrator gave you."
      footer={
        setup?.selfRegistrationOpen ? (
          <>
            No account yet? <Link to="/register">Create one</Link>
          </>
        ) : (
          <span className="muted">Accounts are created by an administrator.</span>
        )
      }
    >
      {setup?.needsFirstAdmin && (
        <InfoAlert>
          This database has no users yet. <Link to="/register">Create the first account</Link> — it becomes the
          administrator.
        </InfoAlert>
      )}

      <ErrorAlert message={failure} />

      <form onSubmit={submit} noValidate>
        <TextField
          type="email"
          label="Email ID"
          placeholder="name@email.com"
          autoComplete="username"
          autoFocus
          full
          {...form.fieldProps('email')}
        />
        <TextField
          type="password"
          label="Password"
          placeholder="Your password"
          autoComplete="current-password"
          full
          {...form.fieldProps('password')}
        />
        <button className="btn primary auth-submit" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  );
}
